// ────────────────────────────────────────────────────────────
// TRACT PAGE — the title source of truth for one TRS
// ────────────────────────────────────────────────────────────
// Owns the chain, worksheet, flags, untraced branches, and the
// party roster with its actions. Deals are thin chips pointing
// here. Party rows wire into MI: expand joins the indexed corpus
// by shared party ID; "Skip trace" injects into MI's existing
// agent flow (useSkipTrace — DD owns none of that machinery).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { DealRecord, PartyResolution, TractParty } from './types';
import { getDealSource, type TractView } from './data/source';
import { useTractRequests } from './data/requests';
import { resolveParties, listDealsForTract } from './data/parties';
import { aliasVariants } from './data/derive';
import { ChainView } from './components/ChainView';
import { StickyStrip } from './components/StickyStrip';
import {
  RequestsPanel, MathNotesPanel, FlagsPanel,
  PartiesPanel, RevisionLogPanel, DealsStrip,
} from './components/Panels';
import { useSkipTrace } from '../mi/components/useSkipTrace';
import { SkipTracePanels } from '../mi/components/SkipTracePanels';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; view: TractView };

export function TractPage() {
  const { trs } = useParams<{ trs: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const requests = useTractRequests(trs);

  // Live thin-deal records (created via Convert to deal) merged with
  // whatever the source already knows about.
  const [liveDeals, setLiveDeals] = useState<DealRecord[]>([]);
  const [liveDealsOk, setLiveDealsOk] = useState(true);

  // Shared MI skip-trace flow — the exact machinery every MI surface
  // uses; DD only feeds it owners.
  const skip = useSkipTrace(trs ? `dd:tract:${trs}` : 'dd:tract:none');

  // Party identity resolutions (matchKey → resolution).
  const [resolutions, setResolutions] = useState<Map<string, PartyResolution> | null>(null);
  const [identityAvailable, setIdentityAvailable] = useState(true);

  useEffect(() => {
    if (!trs) return;
    let alive = true;
    setState({ kind: 'loading' });
    getDealSource().getTract(trs)
      .then(view => { if (alive) setState({ kind: 'ready', view }); })
      .catch(e => { if (alive) setState({ kind: 'error', message: (e as Error).message }); });
    return () => { alive = false; };
  }, [trs]);

  const refreshLiveDeals = useCallback(() => {
    if (!trs) return;
    listDealsForTract(trs)
      .then(r => { setLiveDeals(r.deals); setLiveDealsOk(true); })
      .catch(() => setLiveDealsOk(false));
  }, [trs]);
  useEffect(() => { refreshLiveDeals(); }, [refreshLiveDeals]);

  // Resolve the traced roster against MI once the tract is loaded.
  useEffect(() => {
    if (state.kind !== 'ready') return;
    let alive = true;
    const roster = state.view.tract.parties.map(p => ({
      name: p.name,
      aliases: aliasVariants(p.name),
    }));
    if (roster.length === 0) { setResolutions(new Map()); return; }
    resolveParties(roster, state.view.tract.trs)
      .then(r => {
        if (!alive) return;
        const map = new Map<string, PartyResolution>();
        state.view.tract.parties.forEach((p, i) => {
          const res = r.resolutions[i];
          if (res) map.set(p.matchKey, res);
        });
        setResolutions(map);
        setIdentityAvailable(true);
      })
      .catch(() => { if (alive) { setIdentityAvailable(false); setResolutions(new Map()); } });
    return () => { alive = false; };
  }, [state]);

  const onJumpToDoc = (docNumber: string) => {
    const el = document.getElementById(`dd-doc-${docNumber}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.remove('dd-flash');
    void (el as HTMLElement).offsetWidth;
    el.classList.add('dd-flash');
    window.setTimeout(() => el.classList.remove('dd-flash'), 1800);
  };

  // After an explicit confirm click, reflect the recorded link in place.
  const onPatchResolution = useCallback((matchKey: string, patch: Partial<PartyResolution>) => {
    setResolutions(prev => {
      if (!prev) return prev;
      const cur = prev.get(matchKey);
      if (!cur) return prev;
      const next = new Map(prev);
      next.set(matchKey, { ...cur, ...patch });
      return next;
    });
  }, []);

  const onSkipTrace = (p: TractParty) => {
    if (!trs) return;
    // Real run, real credits: the agent crawls the recorder and runs paid
    // skip-trace lookups the moment we submit (and loads to Close when the
    // checkbox is on). MI's surfaces fire without asking; on this page rows
    // get test-clicked, so confirm first.
    const ok = window.confirm(
      `Skip trace ${p.name}?\n\nThis submits a real agent run (recorder crawl + paid skip-trace lookups${''
      }, load-to-Close per the checkbox in the run panel). It is not a preview.`,
    );
    if (!ok) return;
    // Same payload shape MI's PartyDetail hands the agent.
    skip.submit(p.name, [{
      name: p.name,
      trs: [trs],
      doc_numbers: p.chainDocNumbers,
      county: 'Weld',
    }]);
  };

  const deals = useMemo(() => {
    if (state.kind !== 'ready') return [];
    const merged = new Map<string, DealRecord>();
    for (const d of state.view.deals) merged.set(d.id, d);
    for (const d of liveDeals) merged.set(d.id, d);
    return [...merged.values()];
  }, [state, liveDeals]);

  if (state.kind === 'loading') return <div className="dd-loading">Loading tract…</div>;
  if (state.kind === 'error') {
    return (
      <div className="dd-error">
        <strong>Could not load this tract</strong>
        {state.message}
      </div>
    );
  }

  const { tract } = state.view;
  const confBand = tract.confidence >= 70 ? 'high' : tract.confidence >= 45 ? 'mid' : 'low';

  return (
    <div className="dd-page">
      <header className="dd-header">
        <div className="dd-header-main">
          <div className="dd-header-toprow">
            <h1 className="dd-owner-name">Tract <em>{tract.trs}</em></h1>
            <span className="dd-stage-chip">titled</span>
          </div>
          <div className="dd-header-meta">
            <span>{tract.county} County, CO</span>
            <span className="sep">·</span>
            <span>{tract.statusLine}</span>
            <span className="sep">·</span>
            <span>titled {fmtDate(tract.titledAt)}</span>
            <span className="sep">·</span>
            <a href={R2_BUCKET_URL} target="_blank" rel="noreferrer"
              title="Flat inbox: drop files as manual/REF-description.pdf; we sort from there">
              Manual inbox (R2) ↗
            </a>
          </div>
        </div>

        <div className="dd-header-stats">
          <div>
            <div className="dd-stat-label">Untraced</div>
            <div className="dd-stat-nma untraced">{fmtAc(tract.untracedTotal)}<span className="unit">of {fmtAc(tract.grossAcres)} ac</span></div>
            <div className="dd-stat-sub">branches never traced</div>
          </div>
          <div>
            <div className="dd-stat-label">Confidence</div>
            <div className={`dd-conf ${confBand}`}>{tract.confidence}<span className="pct">%</span></div>
            <div className={`dd-conf-band ${confBand}`}>{confBand === 'low' ? 'LOW' : confBand === 'mid' ? 'MODERATE' : 'HIGH'}</div>
          </div>
        </div>

        {tract.confidenceReasons.length > 0 && (
          <div className="dd-conf-reasons">
            {tract.confidenceReasons.map((r, i) => (
              <div key={i} className="dd-conf-reason">{r}</div>
            ))}
          </div>
        )}
      </header>

      <StickyStrip>
        <span className="who">Tract {tract.trs}</span>
        <span className="sep">·</span>
        <span className="nma untraced-n">{fmtAc(tract.untracedTotal)} of {fmtAc(tract.grossAcres)} ac untraced</span>
        <span className="sep">·</span>
        <span className={`conf ${confBand}`}>{tract.confidence}% {confBand === 'low' ? 'LOW' : confBand === 'mid' ? 'MODERATE' : 'HIGH'}</span>
      </StickyStrip>

      <DealsStrip deals={deals} />
      {!liveDealsOk && (
        <div className="dd-request-unavailable" style={{ margin: '6px 2px 0' }}>
          Deal records for this tract couldn't be refreshed just now.
        </div>
      )}

      <SkipTracePanels skip={skip} />

      <div className="dd-columns">
        <div>
          <ChainView tract={tract} />
        </div>
        <div>
          <RequestsPanel requests={requests} />
          <PartiesPanel
            tract={tract}
            requests={requests}
            resolutions={resolutions}
            identityAvailable={identityAvailable}
            onSkipTrace={onSkipTrace}
            onConverted={refreshLiveDeals}
            onPatchResolution={onPatchResolution}
          />
          {/* The NMA worksheet is subject-specific (it resolves to one
              party's share) — it lives on the deal page only. The tract's
              reservation math stays visible in the chain rail. */}
          <MathNotesPanel tract={tract} />
          <FlagsPanel tract={tract} onJumpToDoc={onJumpToDoc} />
          <RevisionLogPanel tract={tract} />
        </div>
      </div>
    </div>
  );
}

const R2_BUCKET_URL =
  'https://dash.cloudflare.com/81fa6facbc9e6e08a7eff1d79d692f6f/r2/default/buckets/timberline-workbench-files';

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtAc(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
}
